import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Ability } from '../../systems/AbilitySystem';
import { gameEvents } from '../../core/EventBus';
import { COLORS } from '../../config/GameConfig';
import { GameEvents } from '../../config/events';
import { AccessibilitySystem } from '../../systems/AccessibilitySystem';
import { particleDensityFactor } from '../../systems/particleScaling';
import { a11yColor3, a11yColor4 } from '../../systems/abilityColorHelpers';

export interface CalmingResonanceVisualConfig {
  ringThickness?: number;
  ringOpacity?: number;
  /** Fade the far edge without reducing the gameplay radius. Default is linear. */
  ringFadeExponent?: number;
  /** Optional local presentation; gameplay range remains spherical. */
  ringPlane?: 'upright' | 'ground';
  ringHeightOffset?: number;
}

/**
 * Calming Resonance (The Purr) — Minna's AoE calming ability (Babylon.js port).
 *
 * When activated, Minna purrs, creating expanding concentric rings
 * of calming energy. Affects:
 * - CrystalReceivers: charges them when in range
 * - Frantic NPCs: calms them
 * - Frightened kittens: soothes them
 * - Agitated wildlife: pacifies them
 *
 * Hold to activate — Minna slows down while purring.
 * Costs nothing but restricts movement.
 *
 * Reduced-motion support:
 *   - particleDensityFactor() scales emitRate at activate() time and is
 *     re-applied live when ACCESSIBILITY_CHANGED fires.
 *   - Particle capacity (ParticleSystem ctor arg) is fixed at construction
 *     time by Babylon — only the NEXT activation will reflect a capacity
 *     change. emitRate can be updated while the system is running.
 */
export class CalmingResonance implements Ability {
  readonly id = 'calmingResonance';
  readonly name = 'Calming Resonance';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 0.3; // seconds

  private scene: Scene;
  private minnaMesh: AbstractMesh;
  private readonly purrRadius = 6; // world units
  private elapsedTime = 0;

  /** Base particle emitRate before reduced-motion scaling. */
  private static readonly BASE_EMIT_RATE = 20;
  /** Base particle capacity before reduced-motion scaling. */
  private static readonly BASE_CAPACITY = 100;

  /**
   * Radius scale factor applied by the energy system.
   * 1.0 = full range, 0.5 = half range (depleted penalty).
   */
  public radiusScale: number = 1.0;

  /** Expanding ring meshes (recycled each frame cycle) */
  private rings: Mesh[] = [];
  private ringMaterials: StandardMaterial[] = [];
  private readonly ringCount = 3;

  /** Particle system for calming particles */
  private particles: ParticleSystem | null = null;

  /** Movement speed multiplier while active (applied externally) */
  readonly moveSpeedMultiplier = 0.4;

  /** Whether the movement slowdown is currently applied (for external systems to check) */
  private _isSlowed = false;

  /** Unsubscribe function for ACCESSIBILITY_CHANGED listener. */
  private _a11yUnsub: (() => void) | null = null;

  constructor(scene: Scene, minnaMesh: AbstractMesh, private readonly visualConfig: CalmingResonanceVisualConfig = {}) {
    this.scene = scene;
    this.minnaMesh = minnaMesh;

    // Live re-tune: update emitRate whenever reduced-motion preference changes.
    // NOTE: capacity is set at ParticleSystem construction time by Babylon.js —
    // changing it here only takes effect on the NEXT activation.
    this._a11yUnsub = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, (settings) => {
      if (this.particles) {
        const factor = particleDensityFactor(settings.reducedMotion);
        this.particles.emitRate = CalmingResonance.BASE_EMIT_RATE * factor;
      }
    });
  }

  get isSlowed(): boolean {
    return this._isSlowed;
  }

  canActivate(): boolean {
    return !this.isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this._isSlowed = true;
    this.elapsedTime = 0;

    const reduced = AccessibilitySystem.getInstance().state.reducedMotion;
    const factor = particleDensityFactor(reduced);
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);

    // Create expanding ring meshes
    const auraColor = a11yColor3('MINNA_AURA', COLORS.MINNA_AURA, rc);

    for (let i = 0; i < this.ringCount; i++) {
      const ring = CreateTorus(`purrRing_${i}`, {
        diameter: 1, // will be scaled dynamically
        thickness: this.visualConfig.ringThickness ?? 0.03,
        tessellation: 48,
      }, this.scene);

      const mat = new StandardMaterial(`purrRingMat_${i}`, this.scene);
      mat.emissiveColor = auraColor;
      mat.alpha = this.visualConfig.ringOpacity ?? 0.5;
      mat.disableLighting = true;
      mat.wireframe = false;

      ring.material = mat;
      ring.isPickable = false;
      ring.position = this.minnaMesh.position.clone();
      ring.position.y += this.visualConfig.ringHeightOffset ?? 0;
      // Babylon toruses lie in XZ by default. Preserve the legacy upright
      // treatment unless a level requests waves along its walking surface.
      ring.rotation.x = this.visualConfig.ringPlane === 'ground' ? 0 : Math.PI / 2;

      this.rings.push(ring);
      this.ringMaterials.push(mat);
    }

    // Create particle system for ambient calming particles.
    // Capacity is fixed at construction time — scaling it requires a new
    // ParticleSystem instance (i.e. next activation). emitRate is tunable live.
    const capacity = Math.max(1, Math.ceil(CalmingResonance.BASE_CAPACITY * factor));
    this.particles = new ParticleSystem('purrParticles', capacity, this.scene);
    this.particles.emitter = this.minnaMesh;
    this.particles.minSize = 0.05;
    this.particles.maxSize = 0.15;
    this.particles.minLifeTime = 1.0;
    this.particles.maxLifeTime = 2.0;
    this.particles.emitRate = CalmingResonance.BASE_EMIT_RATE * factor;
    this.particles.minEmitPower = 0.1;
    this.particles.maxEmitPower = 0.5;
    this.particles.direction1 = new Vector3(-2, 0.5, -2);
    this.particles.direction2 = new Vector3(2, 1.5, 2);
    this.particles.color1 = a11yColor4('MINNA_AURA', '#00ff88', 0.6, rc);
    this.particles.color2 = a11yColor4('BIOLUMINESCENT_GREEN', '#00ff88', 0.4, rc);
    this.particles.colorDead = a11yColor4('MINNA_AURA', '#00ff88', 0, rc);
    this.particles.createSphereEmitter(this.purrRadius * 0.4);
    this.particles.blendMode = ParticleSystem.BLENDMODE_ADD;
    this.particles.start();

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    this.elapsedTime += deltaTime;

    const pulse = Math.sin(this.elapsedTime * 4) * 0.3 + 0.7;

    // Update expanding concentric rings
    for (let i = 0; i < this.ringCount; i++) {
      const ring = this.rings[i];
      const mat = this.ringMaterials[i];
      if (!ring || !mat) continue;

      // Each ring expands at a phase offset, then resets
      const ringOffset = ((this.elapsedTime + i * 0.33) % 1);
      const effectiveRadius = this.purrRadius * this.radiusScale;
      const scale = effectiveRadius * 2 * ringOffset;
      const alpha = Math.pow(1 - ringOffset, this.visualConfig.ringFadeExponent ?? 1)
        * (this.visualConfig.ringOpacity ?? 0.4) * pulse;

      ring.scaling.set(scale, scale, scale);
      ring.position.copyFrom(this.minnaMesh.position);
      ring.position.y += this.visualConfig.ringHeightOffset ?? 0;
      mat.alpha = alpha;
    }

    // PURR_PULSE fires every frame while the purr is active — it drives
    // per-frame VFX + per-frame proximity checks by listening systems.
    // NPC_CALMED is reserved for the one-shot semantic transition
    // emitted by NPC.ts when an NPC flips to the calmed state.
    gameEvents.emit(GameEvents.PURR_PULSE, {
      sourcePosition: { x: this.minnaMesh.position.x, y: this.minnaMesh.position.y, z: this.minnaMesh.position.z },
      radius: this.purrRadius * this.radiusScale,
    });
  }

  deactivate(): void {
    this.isActive = false;
    this._isSlowed = false;
    this.cooldownRemaining = this.cooldownDuration;

    // Dispose rings
    for (const ring of this.rings) {
      ring.dispose();
    }
    this.rings = [];

    for (const mat of this.ringMaterials) {
      mat.dispose();
    }
    this.ringMaterials = [];

    // Stop and dispose particles
    if (this.particles) {
      this.particles.stop();
      this.particles.dispose();
      this.particles = null;
    }
  }

  dispose(): void {
    this.deactivate();
    if (this._a11yUnsub) {
      this._a11yUnsub();
      this._a11yUnsub = null;
    }
  }

  /**
   * Get the purr radius for external range checking.
   */
  getRadius(): number {
    return this.purrRadius;
  }
}
